import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { api } from "@/lib/api";
import { formatCurrency, formatPercent } from "@/lib/format";
import { useCountUp } from "@/hooks/useCountUp";
import ProjectionChart from "@/components/ProjectionChart";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_aa4f8546-5d46-42d2-8104-f84919deefe1/artifacts/prtdjqf5_NWP%20white.png";

const INITIAL_MIN = 10;
const INITIAL_MAX = 1_000_000;
const ADDON_MIN = 10;
const ADDON_MAX = 1_000_000;

function CountUpAmount({ value }) {
  const animated = useCountUp(value, 700);
  return <span className="nwp-numeric">{formatCurrency(animated)}</span>;
}

function SnapshotCard({ month, snap, delay, t }) {
  return (
    <div
      data-testid={`snapshot-card-${month}`}
      className="nwp-card nwp-card-hover nwp-fade-up p-5 sm:p-6"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="nwp-eyebrow mb-3">{t("calculator.month_eyebrow", { count: month })}</div>
      <div className="text-[11px] mb-1" style={{ color: "var(--nwp-text-muted)" }}>
        {t("calculator.card_gesamtinvest")}
      </div>
      <div
        className="nwp-big-number text-xl sm:text-2xl mb-4"
        style={{ color: "#fff" }}
        data-testid={`snapshot-invest-${month}`}
      >
        <CountUpAmount value={snap.invest} />
      </div>
      <div className="text-[11px] mb-1" style={{ color: "var(--nwp-text-muted)" }}>
        {t("calculator.card_monthly_interest")}
      </div>
      <div className="text-sm" style={{ color: "var(--nwp-gold)" }} data-testid={`snapshot-monthly-${month}`}>
        <CountUpAmount value={snap.monthly_interest_net} />
      </div>
      <div
        className="mt-3 pt-3 text-[11px]"
        style={{ borderTop: "1px dashed rgba(255,255,255,0.08)", color: "var(--nwp-text-muted)" }}
      >
        {t("calculator.card_cumulative_interest")}
      </div>
      <div
        className="text-sm"
        style={{ color: "#e8e8e8" }}
        data-testid={`snapshot-cum-interest-${month}`}
      >
        <CountUpAmount value={snap.cumulative_interest_net ?? 0} />
      </div>
    </div>
  );
}

export default function Calculator() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [initial, setInitial] = useState(5000);
  const [addon, setAddon] = useState(100);
  const [reinvest, setReinvest] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const validateInitial = (value) => {
    if (value === "" || value === null || value === undefined) return t("calculator.validation_initial_required");
    const n = Number(value);
    if (Number.isNaN(n)) return t("calculator.validation_invalid_number");
    if (n < INITIAL_MIN || n > INITIAL_MAX) return t("calculator.validation_initial_range");
    return "";
  };

  const validateAddon = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const n = Number(value);
    if (Number.isNaN(n)) return t("calculator.validation_invalid_number");
    if (n === 0) return "";
    if (n < ADDON_MIN || n > ADDON_MAX) return t("calculator.validation_addon_range");
    return "";
  };

  const initialError = useMemo(() => validateInitial(initial), [initial, t]); // eslint-disable-line
  const addonError = useMemo(() => validateAddon(addon), [addon, t]); // eslint-disable-line
  const isValid = !initialError && !addonError;

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    navigate("/", { replace: true });
  };

  const handleCalculate = async () => {
    if (!isValid) return;
    setLoading(true);
    try {
      const payload = {
        initial_invest: Number(initial),
        weekly_addon: addon === "" || addon === null ? null : Number(addon),
        reinvest: !!reinvest,
      };
      const res = await api.post("/calculate", payload);
      setResult(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error(t("calculator.toast_session_expired"));
        navigate("/", { replace: true });
      } else {
        toast.error(t("calculator.toast_calc_failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="calculator-page" className="min-h-screen">
      {/* Top bar */}
      <header
        className="sticky top-0 z-30 flex items-center justify-between px-5 py-4 md:px-10"
        data-testid="topbar"
        style={{
          background: "rgba(10,10,10,0.75)",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid var(--nwp-border)",
        }}
      >
        <img src={LOGO_URL} alt="NWP" className="h-9 w-auto" data-testid="nwp-logo" />
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="nwp-btn-ghost text-xs px-3 py-2 rounded-lg flex items-center gap-1.5"
          >
            <LogOut size={13} />
            <span>{t("calculator.logout")}</span>
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-10 md:px-10 md:py-14">
        <div className="nwp-fade-up mb-10">
          <div className="nwp-eyebrow mb-2" style={{ color: "var(--nwp-gold)" }}>
            {t("calculator.eyebrow")}
          </div>
          <h1
            data-testid="page-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-light"
            style={{ letterSpacing: "-0.02em", color: "#fff" }}
          >
            {t("calculator.heading")}
          </h1>
          <p className="text-sm mt-3" style={{ color: "var(--nwp-text-muted)" }}>
            {t("calculator.subtitle")}
          </p>
          <p
            data-testid="month-convention-hint"
            className="text-xs mt-1 italic"
            style={{ color: "rgba(255,255,255,0.40)" }}
          >
            {t("calculator.convention_hint")}
          </p>
        </div>

        {/* Inputs */}
        <section
          data-testid="inputs-section"
          className="nwp-card nwp-fade-up p-6 md:p-8 mb-8"
          style={{ animationDelay: "60ms" }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            <div data-testid="initial-invest-field">
              <div className="flex items-baseline justify-between mb-3">
                <label className="text-xs uppercase tracking-wider" style={{ color: "var(--nwp-text-dim)", letterSpacing: "0.12em" }}>
                  {t("calculator.initial_label")}
                </label>
                <span className="text-[10px]" style={{ color: "#6b6b6b" }}>
                  {t("calculator.range_initial")}
                </span>
              </div>
              <div className="flex items-center gap-2 mb-4">
                <Input
                  type="number"
                  data-testid="initial-invest-input"
                  value={initial}
                  onChange={(e) => {
                    const v = e.target.value;
                    setInitial(v === "" ? "" : Number(v));
                  }}
                  min={INITIAL_MIN}
                  max={INITIAL_MAX}
                  step={10}
                  className="nwp-input h-11 text-base"
                />
                <span className="text-sm w-4" style={{ color: "var(--nwp-text-muted)" }}>$</span>
              </div>
              <Slider
                data-testid="initial-invest-slider"
                value={[Number(initial) || INITIAL_MIN]}
                min={INITIAL_MIN}
                max={INITIAL_MAX}
                step={10}
                onValueChange={(vals) => setInitial(vals[0])}
              />
              {initialError && (
                <p className="text-xs mt-2" style={{ color: "#ff5a55" }} data-testid="initial-invest-error">
                  {initialError}
                </p>
              )}
            </div>

            {/* Wöchentlicher Zusatzinvest */}
            <div data-testid="weekly-addon-field">
              <div className="flex items-baseline justify-between mb-3">
                <label className="text-xs uppercase tracking-wider" style={{ color: "var(--nwp-text-dim)", letterSpacing: "0.12em" }}>
                  {t("calculator.addon_label")}
                </label>
                <span className="text-[10px]" style={{ color: "#6b6b6b" }}>
                  {t("calculator.range_addon")}
                </span>
              </div>
              <div className="flex items-center gap-2 mb-4">
                <Input
                  type="number"
                  data-testid="weekly-addon-input"
                  value={addon === null ? "" : addon}
                  onChange={(e) => {
                    const v = e.target.value;
                    setAddon(v === "" ? "" : Number(v));
                  }}
                  min={0}
                  max={ADDON_MAX}
                  step={10}
                  placeholder={t("calculator.addon_placeholder")}
                  className="nwp-input h-11 text-base"
                />
                <button
                  type="button"
                  data-testid="weekly-addon-clear-button"
                  onClick={() => setAddon(null)}
                  className="nwp-btn-ghost text-xs px-3 py-2 rounded-lg whitespace-nowrap"
                >
                  {t("calculator.clear")}
                </button>
              </div>
              <Slider
                data-testid="weekly-addon-slider"
                value={[addon === "" || addon === null ? 0 : Number(addon)]}
                min={0}
                max={ADDON_MAX}
                step={10}
                onValueChange={(vals) => setAddon(vals[0])}
              />
              {addonError && (
                <p className="text-xs mt-2" style={{ color: "#ff5a55" }} data-testid="weekly-addon-error">
                  {addonError}
                </p>
              )}
            </div>
          </div>

          {/* Reinvest */}
          <div
            className="mt-8 rounded-xl px-5 py-4"
            style={{ background: "rgba(0,0,0,0.35)", border: "1px solid var(--nwp-border)" }}
            data-testid="reinvest-field"
          >
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="text-sm font-medium" style={{ color: "#fff" }}>
                  {t("calculator.reinvest_label")}
                </div>
                <div className="text-[11px] mt-0.5" style={{ color: "var(--nwp-text-muted)" }}>
                  {t("calculator.reinvest_desc")}
                </div>
              </div>
              <span
                className="text-xs uppercase tracking-wider px-2 py-1 rounded-full"
                style={{
                  background: reinvest ? "rgba(225,6,0,0.12)" : "rgba(255,255,255,0.04)",
                  color: reinvest ? "#ff5a55" : "#6b6b6b",
                  letterSpacing: "0.12em",
                  border: `1px solid ${reinvest ? "rgba(225,6,0,0.30)" : "var(--nwp-border)"}`,
                }}
                data-testid="reinvest-state-label"
              >
                {reinvest ? t("calculator.state_on") : t("calculator.state_off")}
              </span>
              <Switch data-testid="reinvest-switch" checked={reinvest} onCheckedChange={setReinvest} />
            </div>
          </div>

          {/* Berechnen */}
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              data-testid="calculate-button"
              onClick={handleCalculate}
              disabled={!isValid || loading}
              className="nwp-btn-primary rounded-xl font-medium px-8"
              style={{ height: 48, fontSize: 15 }}
            >
              {loading ? t("calculator.calculating") : t("calculator.calculate")}
            </button>
          </div>
        </section>

        {/* Results */}
        {result && (
          <section data-testid="results-section" className="space-y-8" key={result.months?.length}>
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
              {[1, 3, 6, 12, 18, 24].map((m, i) => {
                const snap = result.snapshots?.[String(m)];
                if (!snap) return null;
                return <SnapshotCard key={m} month={m} snap={snap} delay={i * 70} t={t} />;
              })}
            </div>

            <div
              data-testid="cumulative-payout-card"
              className="nwp-card nwp-fade-up p-6 sm:p-8"
              style={{
                background:
                  "linear-gradient(135deg, rgba(40,8,8,0.55) 0%, rgba(20,20,20,0.85) 50%, rgba(20,20,20,0.85) 100%)",
                borderColor: "rgba(225,6,0,0.22)",
                animationDelay: "500ms",
              }}
            >
              <div className="nwp-eyebrow mb-5">{t("calculator.summary_eyebrow", { count: 24 })}</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5 sm:gap-6">
                <div data-testid="summary-gesamtinvest-end">
                  <div className="text-[11px] mb-1 uppercase tracking-wider" style={{ color: "var(--nwp-text-muted)", letterSpacing: "0.10em" }}>
                    {t("calculator.summary_gesamtinvest_end")}
                  </div>
                  <div className="nwp-big-number text-xl sm:text-2xl md:text-3xl" style={{ color: "#fff" }}>
                    <CountUpAmount value={result.summary?.gesamtinvest_end ?? 0} />
                  </div>
                </div>
                <div data-testid="summary-cumulative-interest">
                  <div className="text-[11px] mb-1 uppercase tracking-wider" style={{ color: "var(--nwp-text-muted)", letterSpacing: "0.10em" }}>
                    {t("calculator.summary_cumulative_interest")}
                  </div>
                  <div className="nwp-big-number text-xl sm:text-2xl md:text-3xl" style={{ color: "#e8e8e8" }}>
                    <CountUpAmount value={result.summary?.cumulative_interest_net_end ?? 0} />
                  </div>
                </div>
                <div data-testid="summary-cumulative-payout">
                  <div className="text-[11px] mb-1 uppercase tracking-wider" style={{ color: "var(--nwp-text-muted)", letterSpacing: "0.10em" }}>
                    {t("calculator.summary_cumulative_payout")}
                  </div>
                  <div className="nwp-big-number text-xl sm:text-2xl md:text-3xl" style={{ color: "#fff" }}>
                    <CountUpAmount value={result.summary?.cumulative_payout_end ?? 0} />
                  </div>
                </div>
                <div data-testid="summary-gewinn-netto">
                  <div className="text-[11px] mb-1 uppercase tracking-wider" style={{ color: "var(--nwp-text-muted)", letterSpacing: "0.10em" }}>
                    {t("calculator.summary_gewinn_netto")}
                  </div>
                  <div
                    className="nwp-big-number text-xl sm:text-2xl md:text-3xl"
                    style={{ color: "#ff5a55" }}
                    data-testid="summary-gewinn-betrag"
                  >
                    <CountUpAmount value={result.summary?.gewinn_netto_betrag ?? 0} />
                  </div>
                  <div
                    className="text-sm mt-1 nwp-numeric"
                    style={{ color: "var(--nwp-gold)" }}
                    data-testid="summary-gewinn-prozent"
                  >
                    {formatPercent(result.summary?.gewinn_netto_prozent ?? 0)}
                  </div>
                </div>
              </div>
              <div className="mt-5 pt-4 text-[11px]" style={{ borderTop: "1px dashed rgba(255,255,255,0.08)", color: "var(--nwp-text-muted)" }}>
                {t("calculator.summary_eingebrachtes_kapital")}:{" "}
                <span className="nwp-numeric" style={{ color: "#cfcfcf" }} data-testid="summary-eingebrachtes-kapital">
                  {formatCurrency(result.summary?.eingebrachtes_kapital ?? 0)}
                </span>
              </div>
            </div>

            {/* Chart */}
            <div
              data-testid="chart-section"
              className="nwp-card nwp-fade-up p-5 sm:p-7"
              style={{ animationDelay: "580ms" }}
            >
              <ProjectionChart months={result.months} />
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
